import React from "react";
import { BookmarkPlus, Check, X } from "lucide-react";
import { isElectronicBookmarkGroupNameTooLong } from "../api/editGropus";
import {
  MODAL_ANIMATION_CSS,
  MODAL_OVERLAY_STYLE,
  MODAL_PANEL_STYLE,
  useBodyScrollLock,
} from "./modalEffects";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";

type SaveBookmarkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  newGroupName: string;
  selectedBookTitle: string | null;
  onTypeNewGroup: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveBookmark: () => void;
  isSavingBookmark: boolean;
  isSaveConfirmed: boolean;
  bookmarkMessage: {
    type: "success" | "error" | "info" | "warning";
    text: string;
  } | null;
};

const SaveBookmarkModal: React.FC<SaveBookmarkModalProps> = ({
  isOpen,
  onClose,
  newGroupName,
  selectedBookTitle,
  onTypeNewGroup,
  onSaveBookmark,
  isSavingBookmark,
  isSaveConfirmed,
  bookmarkMessage,
}) => {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const isGroupNameTooLong = isElectronicBookmarkGroupNameTooLong(newGroupName);

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm"
      style={MODAL_OVERLAY_STYLE}
    >
      <style>{MODAL_ANIMATION_CSS}</style>
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-[30px] border border-white/35 bg-[linear-gradient(180deg,rgba(255,252,248,0.98),rgba(248,250,252,0.98))] shadow-[0_40px_120px_rgba(15,23,42,0.24)]"
        style={MODAL_PANEL_STYLE}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/90 text-slate-700 shadow-sm transition hover:bg-white"
          aria-label="Zamknij"
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>

        <div className="border-b border-slate-200/80 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,248,240,0.9))] px-5 py-6 sm:px-7">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] border border-white/60 bg-white/80 text-[#f6851f] shadow-[0_18px_40px_rgba(246,133,31,0.16)]">
              <BookmarkPlus size={24} strokeWidth={2.1} aria-hidden="true" />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500">
                Zapisz zakładkę
              </p>
              <h2 className="mt-1 text-2xl font-semibold tracking-[-0.04em] text-slate-950">
                Nowa grupa
              </h2>
              {selectedBookTitle && (
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {selectedBookTitle}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-7">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Utórz grupę, aby dodać zakładkę
          </label>
          <input
            type="text"
            value={newGroupName}
            onChange={onTypeNewGroup}
            placeholder="Np. Poniedziałek 17:00 B1"
            className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none transition focus:border-[#f6851f] focus:ring-4 focus:ring-[#f6851f]/10 ${
              isGroupNameTooLong ? "text-red-600" : "text-slate-900"
            }`}
          />

          {bookmarkMessage && (
            <div
              className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                bookmarkMessage.type === "error"
                  ? "bg-red-50 text-red-700"
                  : bookmarkMessage.type === "success"
                    ? "bg-emerald-50 text-emerald-700"
                    : bookmarkMessage.type === "warning"
                      ? "border border-amber-300 bg-amber-50 text-amber-900"
                      : "bg-slate-100 text-slate-700"
              }`}
            >
              {bookmarkMessage.text}
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onSaveBookmark}
              disabled={isSavingBookmark || isSaveConfirmed}
              className={`inline-flex items-center gap-2 rounded-full px-6 py-3 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
              style={getActionButtonStyle("orange")}
            >
              {isSaveConfirmed ? (
                <>
                  <Check size={18} strokeWidth={2.6} aria-hidden="true" />
                  Zapisano
                </>
              ) : isSavingBookmark ? (
                "Zapisywanie..."
              ) : (
                "Zapisz"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveBookmarkModal;
